import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getCompany } from '../../../redux/companySlice';
import { ENDPOINTS, CONSTS } from '../../pages/utils/endpoints';
import MaterialRow from './MaterialRow';
import './MaterialsTable.scss';

// Single-content-type list view behind the "list" toggle. Fed by a page's
// already-category-filtered data, so it only ever contains one material type.
// Columns are click-to-sort; numeric columns default to descending.
const COLUMNS = [
  { key: 'name', label: 'Name', numeric: false, align: 'left' },
  { key: 'status', label: 'Status', numeric: false, align: 'left' },
  { key: 'opens', label: 'Opens', numeric: true, align: 'right' },
  { key: 'completions', label: 'Completions', numeric: true, align: 'right' },
  { key: 'unique_visits', label: 'Unique', numeric: true, align: 'right' }
];

const baseUrlFor = (item, companySlug) => {
  switch (String(item.material_type || '').toLowerCase()) {
    case CONSTS.WALKTHROUGH:
    case CONSTS.VIDEO:
      return ENDPOINTS.VIEW_WALKTHROUGH(companySlug, item.slug);
    case CONSTS.CHECKLISTS:
      return ENDPOINTS.VIEW_CHECKLIST(companySlug, item.slug);
    case CONSTS.DOCS:
      return ENDPOINTS.VIEW_DOC(companySlug, item.slug);
    case CONSTS.PATHS:
    default:
      return ENDPOINTS.VIEW_PATH(companySlug, item.slug);
  }
};

const Caret = ({ active, dir }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true"
       style={{ marginLeft: 5, opacity: active ? 1 : 0.28, transform: active && dir === 'asc' ? 'rotate(180deg)' : 'none' }}>
    <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MaterialsTable = ({ data = [], setUpdatedData }) => {
  const company = useSelector((state) => getCompany(state));
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const onSort = (col) => {
    if (col.key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir(col.numeric ? 'desc' : 'asc');
    }
  };

  const sorted = useMemo(() => {
    const numeric = COLUMNS.find((c) => c.key === sortKey)?.numeric;
    const rows = [...data].sort((a, b) => {
      if (numeric) return (Number(a[sortKey]) || 0) - (Number(b[sortKey]) || 0);
      const av = String(a[sortKey] ?? '').toLowerCase();
      const bv = String(b[sortKey] ?? '').toLowerCase();
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
    return sortDir === 'desc' ? rows.reverse() : rows;
  }, [data, sortKey, sortDir]);

  return (
    <div className="mt-wrap">
      <table className="mt-table">
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th
                key={c.key}
                className={`${c.align === 'right' ? 'mt-r' : ''} ${sortKey === c.key ? 'is-sorted' : ''}`}
                onClick={() => onSort(c)}
              >
                <span className="mt-th">{c.label}<Caret active={sortKey === c.key} dir={sortDir} /></span>
              </th>
            ))}
            <th className="mt-actions-head" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((item) => (
            <MaterialRow
              key={item.slug || item.id}
              item={item}
              companySlug={company?.slug}
              baseUrl={baseUrlFor(item, company?.slug)}
              setUpdatedData={setUpdatedData}
            />
          ))}
          {sorted.length === 0 && (
            <tr><td className="mt-empty" colSpan={6}>Nothing here yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MaterialsTable;
